<template>
  <div class="container-fluid max-container">
    <div class="row">
      <Header />
      <section>
        <div class="container">
          <div class="archaic-upgrade-container-fluid">
            <h1>Living DNA Store</h1>
            <!-- Personalised vitamins without Wellbeing results, and anything an epigenetics profile can't
                 have, have their own prompt by the price -->
            <div v-if="!canPurchase && !vitaminsNeedWellbeing && !showAncestryRequired" class="alert alert-danger mt-4"
              role="alert">
              <span v-if="upgradeBlockedFor === 'testing_failed' && profileBarcode?.toUpperCase().startsWith('LT')">
                We couldn't process the DNA file uploaded for kit {{ profileBarcode }}, so this upgrade would have no
                results to add to. Contact <a href="mailto:help@livingdna.com">help@livingdna.com</a> and we'll help.
              </span>
              <span v-else-if="upgradeBlockedFor === 'testing_failed'">
                We couldn't get a reliable result from the sample for kit {{ profileBarcode }}, so this upgrade would
                have no results to add to.
                <router-link :to="{ name: 'new-sample-kit', params: { barcode: profileBarcode } }">Request a new sample kit</router-link>.
              </span>
              <span v-else-if="upgradeBlockedFor === 'transfer_qc_failed'">
                The DNA file uploaded for kit {{ profileBarcode }} didn't pass our checks, so this upgrade would have no
                results to add to.
                <router-link :to="{ name: 'reupload-dna', params: { barcode: profileBarcode } }">Upload it again</router-link>.
              </span>
              <span v-else-if="upgradeBlockedFor === 'cancelled'">
                Kit {{ profileBarcode }} was cancelled, so this upgrade would have no results to add to.
              </span>
              <!-- Not for a report already bought: that has its own line below -->
              <span v-else-if="reportWaitsForResults && !alreadyPurchased">
                A genetic health report is made from your DNA results, so you can order one once kit
                {{ profileBarcode }} has them.
                <router-link :to="{ name: 'recent-overview' }">See where it's up to</router-link>.
              </span>
              <span v-else-if="productPrice.product_type === 'Genetic Test' && alreadyPurchased">
                You already have this report for {{ $store.getters.profile.profile.name }}'s profile. If its
                traits still show as locked, your results are being prepared.
              </span>
              <span v-else-if="productPrice.category === 'Digital upgrade' && alreadyPurchased">
                You already have this upgrade for {{ $store.getters.profile.profile.name }}'s profile. Switch profiles
                or
                choose a different upgrade.
              </span>
              <span v-else-if="productPrice.category === 'Digital upgrade'">
                This upgrade isn't available for {{ $store.getters.profile.profile.name }}'s profile. It needs results
                this kit doesn't provide - if you think that's wrong, contact support with the profile's barcode.
              </span>
              <span v-else>
                You need to purchase the Wellbeing upgrade first to avail of the Personalised Vitamins services. <a
                  href="#." class="no-underline" @click="upgradeWellbeing">Click here to purchase.</a>
              </span>
            </div>
            <!-- 70 30 col row starts here -->
            <PageLoader v-if="loadingStatus === 'loading'" />
            <div class="blood-details-grid-row">
              <div>
                <!-- another row -->
                <div class="row">
                  <div class="col-lg-7 col-md-6">
                    <div class="image-details-row">
                    <!-- Main Image/Video Carousel -->
                      <Carousel v-bind="galleryConfig" v-model="currentSlide">
                        <!-- Image Slides -->
                        <Slide v-for="(img, index) in productPrice.images" :key="'img-' + index">
                          <div class="carousel__item" @click="slideTo(index)">
                            <img :src="img.urlDetailed" alt="Gallery Image" class="gallery-image" />
                          </div>
                        </Slide>

                        <!-- Video Slides -->
                        <Slide v-for="(video, index) in productPrice.videos" :key="'vid-' + index" >
                          <div class="carousel__item video-slide">
                            <iframe :src="getVimeoEmbedUrl(video.videoUrl)" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen class="gallery-video" />
                          </div>
                        </Slide>
                      </Carousel>

                      <!-- Thumbnail Carousel -->
                      <Carousel class="thumbnail-slider" v-bind="thumbnailsConfig" v-model="currentSlide">
                        <!-- Image Thumbnails -->
                        <Slide v-for="(img, index) in productPrice.images" :key="'thumb-img-' + index" >
                          <div class="thumbnail__item" @click="slideTo(index)">
                            <img :src="img.urlThumbnail" alt="Thumbnail Image" class="thumbnail-image" />
                          </div>
                        </Slide>

                        <!-- Video Thumbnails -->
                        <Slide v-for="(video, index) in productPrice.videos" :key="'thumb-vid-' + index" >
                          <div class="thumbnail__item video-thumbnail" @click="slideTo(productPrice.images.length + index)">
                            <img :src="getVimeoThumbnail(video.videoUrl)" alt="Video Thumbnail" class="thumbnail-image"/>
                            <!-- Play icon overlay -->
                            <div class="play-icon-overlay">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="32" height="32">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                        </Slide>
                      </Carousel>
                    </div>
                  </div>
                  <div class="col-lg-5 col-md-6">
                    <div class="image-content-row">
                      <h3>
                        {{ productPrice.title }}
                      </h3>
                      <h4 v-if="priceText">
                        {{ displayCurrencySymbol }} {{ priceText }}
                      </h4>
                      <span v-if="preprContent?.product_descriptor_list" v-html="preprContent.product_descriptor_list"></span>
                      <!-- cta container -->
                      <AncestryKitRequiredPrompt v-if="showAncestryRequired" class="mt-3" :product="productPrice.title" />
                      <WellbeingRequiredPrompt v-else-if="vitaminsNeedWellbeing" class="mt-3" />
                      <template v-else>
                        <div v-if="canPurchase" class="cta-order">
                          <button @click="openCheckoutModal" :disabled="loadingVitaminReport">
                            {{ loadingVitaminReport ? 'Loading...' : 'Purchase' }}
                          </button>
                        </div>
                        <div v-else class="cta-order">
                          <button @click="returnStore">Return to store</button>
                        </div>
                      </template>
                      <!-- cta container -->
                    </div>
                  </div>
                </div>
                <!-- another row -->

                <!-- content row -->
                <div class="content-ancestry-kit-row" v-if="featureBulletPoints.length">
                  <h3>You will receive:</h3>
                  <ul class="feature-bullets">
                    <li v-for="(point, index) in featureBulletPoints" :key="index" v-html="point"></li>
                  </ul>
                </div>
                <!-- content row -->

                <PreprProductDetails v-if="preprContent" :product="preprContent" />
              </div>
              <div>
                <PreprProductExtras v-if="preprContent" :product="preprContent" />
                <template v-else>
                <!-- Description Component starts here -->
                <div class="description-row-ancestry-details">
                  <h2>Description</h2>
                  <p v-html="productPrice.longDescription"></p>
                </div>
                <!-- Description Component ends here -->

                <!-- FAQS Component starts here -->
                <div class="faqs-details-blood-row" v-if="productPrice.faq?.length">
                  <h2>FAQs</h2>
                  <!-- accoridion component  -->
                  <nav class="accordion arrows">
                    <div v-for="(faq, index) in productPrice.faq" :key="index">
                      <input type="radio" name="accordion" :id="`cb1${index}`" />
                      <section class="box">
                        <label class="box-title" :for="`cb1${index}`">{{
                          faq.question
                        }}</label>
                        <label class="box-close" for="acc-close"></label>
                        <div class="box-content" v-html="faq.answer"></div>
                      </section>
                    </div>
                  </nav>
                  <!-- accordion component -->
                </div>
                <!-- FAQS Component ends here -->
                </template>
              </div>
            </div>
            <!-- 70 30 col row ends here -->
          </div>
        </div>
      </section>
    </div>

    <!-- Modal component -->
    <CheckoutModal :isModalVisible="isCheckoutVisible" @close="closeCheckoutModal"
      @openPaymentModal="openPaymentModal"
      @openPaymentCardModal="openPaymentCardModal" :contactDetails="contactDetails"
      @update:address="handleAddressUpdate" @back="back" :region="selectedRegion" />

    <!-- PaymentModal / PaymentCardModal each render their own ModalView (gated on
         isModalVisible), so they must NOT be wrapped in another ModalView — the double
         nesting left the inner content unmounted (blank modal / Stripe card elements
         never mounting → "Payment form is not ready"). Mirrors the HealthStore wiring. -->
    <PaymentModal :isModalVisible="isPaymentVisible" @openPaymentCardModal="openPaymentCardModal"
      @openCheckoutModal="openCheckoutModalFromPayment" :contactDetails="contactDetails" :product="productData"
      @back="PaymentcardbackModal" @closePaymentPaypalModal="closePaymentPaypalModal" @order="order"
      @close="closePaymentModal" />

    <PaymentCardModal :isModalVisible="isPaymentCardVisible" :contactDetails="contactDetails" :product="productData"
      @back="PaymentbackModal" :delivered="delivered" @order="order" @closeCardModal="closeCardModal"
      @closePaymentCardModal="closePaymentCardModal" />

    <ModalView :isVisible="isSuccessVisible" :title="'Payment'" :showCloseButton="false">
      <SuccessModal :orderKey="orderKey" />
    </ModalView>

    <ModalView :isVisible="isFailedVisible" :title="'Payment'" @close="closeFailedModal">
      <FailedModal @close="closeFailedModal" />
    </ModalView>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from "vue-router";
import Header from "@/components/header/Header.vue";
import CheckoutModal from "./Modal/CheckoutModal.vue";
import ModalView from "@/components/Modal/ModalView.vue";
import PaymentCardModal from "@/view/health/Blood/Modal/PaymentCard.vue";
import SuccessModal from "./Modal/SuccessModal.vue";
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';

// Import the functions correctly (assuming they exist in the specified path)
import { getVitaminReport, createVitlabUrl } from '@/services/wellbeing/vitlab';

import ProductService from "@/services/product";
import { getAllProducts as getPreprStoreProducts, getProductData as getPreprProductPage } from "@/store/store/store";
import PreprProductDetails from "@/components/store/PreprProductDetails.vue";
import PreprProductExtras from "@/components/store/PreprProductExtras.vue";
import PaymentModal from "@/view/health/Blood/Modal/PaymentModal.vue";
import FailedModal from './Modal/FailedModal.vue';
import store from '@/store';
import { statusWithoutResults } from "@/services/kitService";
import { useAncestryKit } from "@/composables/useAncestryKit";
import WellbeingRequiredPrompt from "@/components/store/WellbeingRequiredPrompt.vue";
import AncestryKitRequiredPrompt from "@/components/store/AncestryKitRequiredPrompt.vue";
const { loadingStatus, finishLoading } = useLoading();
import { useLoading } from "@/composables/useLoading";

const route = useRoute();
const router = useRouter();

// Region-aware pricing — mirrors the logic in HealthStore.vue / store/store/store.js
const REGION_STORAGE_KEY = "store_region_override";
const ALLOWED_REGIONS = ["GBR", "USA", "AUS", "EUR", "CAN", "INT"];
const CURRENCY_BY_REGION = { GBR: "GBP", USA: "USD", AUS: "AUD", EUR: "EUR", CAN: "CAD", INT: "USD" };
// Representative country per region so a digital upgrade (no address form) still resolves the
// correct currency in the order (createOrderObject derives currency from countryCode).
const COUNTRY_BY_REGION = { GBR: "GB", USA: "US", AUS: "AU", EUR: "DE", CAN: "CA", INT: "US" };
const SYMBOL_BY_CURRENCY = { GBP: "£", USD: "$", AUD: "A$", EUR: "€", CAD: "C$" };
const PRICE_KEY_BY_REGION = { GBR: "price", USA: "usa_price", AUS: "aus_price", EUR: "eur_price", CAN: "can_price", INT: "usa_price" };

const storedRegion = ref(localStorage.getItem(REGION_STORAGE_KEY));
const syncStoredRegion = () => { storedRegion.value = localStorage.getItem(REGION_STORAGE_KEY); };
// Keep in sync if the user changes region in another tab
window.addEventListener("storage", syncStoredRegion);
onUnmounted(() => window.removeEventListener("storage", syncStoredRegion));

const selectedRegion = computed(() => {
  const r = storedRegion.value;
  if (r && ALLOWED_REGIONS.includes(r)) return r;
  return store.getters?.profile?.profile?.region || "GBR";
});

/**
 * The symbol belonging to the price actually shown.
 *
 * ecommerce resolves Prepr products for the requested region and sends the symbol with
 * them, so prefer that: deciding it here from the store switcher, while the variant the
 * payment modal renders was priced from somewhere else, is what made one product read
 * £15 on the page and €15 at checkout. The map is the fallback for Craft products, which
 * still carry their own currency on the variant.
 */
const displayCurrencySymbol = computed(() => {
  const resolved = productData.value?.pricing?.currencySymbol?.raw
    || productData.value?.variants?.[0]?.currencySymbol?.raw;
  if (resolved) return resolved;
  const code = CURRENCY_BY_REGION[selectedRegion.value] || "GBP";
  return SYMBOL_BY_CURRENCY[code] || "£";
});

// Digital upgrades ship nothing, so they skip the address step. Handles the singular
// `category` (getProduct path) and plural `categories` (list/epigenetic path).
const isDigitalUpgrade = computed(() => {
  const cat = productData.value?.category ?? productData.value?.categories;
  return cat === "Digital upgrade";
});

const productPrice = ref({ images: [], videos: [] }); // Will be populated by API data
const isCheckoutVisible = ref(false);
const contactDetails = ref({});
const delivered = ref({});
const isPaymentVisible = ref(false);
const isPaymentCardVisible = ref(false);
const productData = ref({});
// The full Prepr page for a Health Report - introduction, tabs, how it works, FAQs,
// testimonials. The store list this page prices from carries none of that, so without it
// the page showed a name, one image and a line of copy, while the same product reached
// from the genetics results showed everything. Null for Craft products.
const preprContent = ref(null);
const isSuccessVisible = ref(false);
const isFailedVisible = ref(false);
const isValid = ref(true);
// The dependency check refuses a sale for two different reasons: the profile already owns
// the upgrade (purchased), or its results can't support it (not purchased). Only the first
// means "you already have this".
const alreadyPurchased = ref(false);
const isEpigenetics = computed(() => store.getters?.profile?.profile?.barcode?.endsWith('E') === true);
// No digital upgrade or genetic health report for a kit whose sample will never give results,
// whatever the store said.
const isGeneticReport = computed(() => productData.value?.product_type === "Genetic Test");
const profileBarcode = computed(() => store.getters?.profile?.profile?.barcode);
const upgradeBlockedFor = computed(() => {
  const test = store.getters.selectedTest;
  return (isDigitalUpgrade.value || isGeneticReport.value) && test?.barcode === profileBarcode.value
    ? statusWithoutResults(test) : null;
});
// A report is made from the kit's or upload's DNA results, so none is sold before they're in
const { pending: kitPending, uploadPending } = useAncestryKit();
const reportWaitsForResults = computed(() =>
  isGeneticReport.value && !isEpigenetics.value && (kitPending.value || uploadPending.value));
// Personalised vitamins are mixed from the Wellbeing results' vitamin mix: nothing to sell without
// one, which the Purchase button used to fail on silently
const vitaminsNeedWellbeing = computed(() =>
  productData.value?.category === "Vitamins supplement" && !isEpigenetics.value &&
  loadingStatus.value !== "loading" && !vitaminReport.value?.vitaminMix);
const canPurchase = computed(() =>
  isValid.value && !upgradeBlockedFor.value && !reportWaitsForResults.value && !vitaminsNeedWellbeing.value);
// True when the slug belongs to a Prepr-sourced epigenetic digital upgrade (not a Craft CMS ancestry product)
const isEpigeneticUpgradeSlug = computed(() => {
  const slug = route.params.slug;
  return typeof slug === 'string' && slug.toLowerCase().includes('epigenetic');
});
// Only show the "Requires an ancestry kit" block for non-epigenetic, non-kit products.
// Kits (ancestry or epigenetic) can always be purchased — no prerequisites.
const showAncestryRequired = computed(() =>
  isEpigenetics.value && !isEpigeneticUpgradeSlug.value && productPrice.value.category !== 'Kit'
);
const orderKey = ref("");
const vitaminReport = ref(null);
const loadingVitaminReport = ref(false);

// The active slide (image) in the carousel
const currentSlide = ref(0);

// Configuration for the main image carousel
const galleryConfig = {
  itemsToShow: 1,
  wrapAround: true,
  mouseDrag: false,
  touchDrag: false,
};

// Configuration for the thumbnail image carousel
const thumbnailsConfig = {
  itemsToShow: 3,
  wrapAround: false,
  gap: 10,
  navigation: false,
  focusOnSelect: true,
};

// Function to switch to a specific slide
const slideTo = (index) => {
  currentSlide.value = index;
}

const loadVitaminReport = async () => {
  if (vitaminReport.value) return vitaminReport.value;

  try {
    loadingVitaminReport.value = true;
    vitaminReport.value = await getVitaminReport(store.getters.profile.profile.barcode);
    return vitaminReport.value;
  } catch (error) {
    console.error('Error loading vitamin report:', error);
    return null;
  } finally {
    loadingVitaminReport.value = false;
  }
};

const extractVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? match[1] : null;
};

const getVimeoEmbedUrl = (url) => {
  const vimeoId = extractVimeoId(url);
  return vimeoId ? `https://player.vimeo.com/video/${vimeoId}` : '';
};

const getVimeoThumbnail = (url) => {
  const vimeoId = extractVimeoId(url);
  return vimeoId
    ? `https://vumbnail.com/${vimeoId}.jpg`
    : '/placeholder-video.png';
};

onMounted(async () => {
  try {
    let resolvedAsUpgrade = false;
    if (isEpigeneticUpgradeSlug.value) {
      // Prepr-sourced epigenetic digital upgrade: not exposed by the Craft
      // single-product endpoint, so resolve it from the list endpoint instead.
      // Physical epigenetic *kits* (category "Kit") share the "epigenetic" slug
      // but are Craft products — they won't match here and fall through to the
      // normal CMS path below (which returns proper variants[] for checkout).
      const barcode = store.getters?.profile?.profile?.barcode;
      // Without the region this fell back to the account's region inside the service,
      // so the price below (read from the store switcher) and the variant handed to the
      // payment modal could describe two different stores - £15 on the page, €15 in the
      // modal, and the order sent in euros.
      //
      // A failed list falls through to the lookups below instead of ending here. The
      // Epigenetic Aging kit only needs the Craft one, which used to be skipped, leaving
      // an empty product card whenever the store list was down.
      let products = [];
      try {
        products = await ProductService.getAllProducts(barcode, {
          category: 'Digital upgrade',
          region: selectedRegion.value,
        });
      } catch (err) {
        console.error("Could not load the digital upgrades; trying the other product sources.", err);
      }
      const match = products?.find(p => p.slug === route.params.slug);
      if (match) {
        const images = match.image ? [{ urlDetailed: match.image, urlThumbnail: match.image }] : [];
        productPrice.value = { ...match, images };
        // Construct a productData shape compatible with the checkout modals
        productData.value = { ...match, variants: [{ sku: match.sku, price: match.price, currency: match.currency, currencySymbol: match.currencySymbol }] };
        resolvedAsUpgrade = true;
      }
      // Epigenetic digital upgrades do not go through the ancestry insights validation
    }
    if (!resolvedAsUpgrade) {
      // A genetic report from the store's Health Reports tab. Like epigenetic upgrades
      // these live in Prepr, so the Craft single-product endpoint below cannot resolve
      // them - they come from the same list that built the store card, which means the
      // price and currency here cannot disagree with the price the customer just clicked.
      //
      // Looked up rather than guessed from the slug: "genetic-aging-plus-upgrade" is an
      // epigenetics product, so a "genetic" prefix would classify it wrongly.
      //
      // Matched by id as well as slug: the genetics results link here with the Prepr id of
      // the category's key product, which is all their query holds.
      //
      // includePurchased: a report this barcode already owns comes back flagged rather than
      // missing, so the page can say so instead of falling through to Craft and breaking.
      const match = (await getPreprStoreProducts({
        barcode: store.getters?.profile?.profile?.barcode,
        region: selectedRegion.value,
        includePurchased: true,
      })).find(p => p.category === "Digital upgrade"
          && [p._slug, p.slug, p._id].includes(route.params.slug));
      if (match) {
        // Content only. Price, SKU, currency and collection method stay on `match`, which
        // ecommerce resolved for the store region; the page from Prepr has no region.
        try {
          preprContent.value = await getPreprProductPage(match._id);
        } catch (err) {
          console.error("Could not load the Health Report's page from Prepr; showing the store summary.", err);
        }
        const content = preprContent.value;
        // This page was written against Craft's product shape, so the Prepr one is
        // adapted to it rather than rendering a page with no title, image or copy.
        const imageUrl = match.header_image?.[0]?.url || match.thumbnail_image?.[0]?.url;
        const contentImages = (content?.thumbnail_image || [])
          .map((img) => img?.url)
          .filter(Boolean)
          .map((url) => ({ urlDetailed: url, urlThumbnail: url }));
        productPrice.value = {
          ...match,
          title: content?.product_name || match.product_name,
          longDescription: match.product_details || match.shortDescription || "",
          images: contentImages.length
            ? contentImages
            : (imageUrl ? [{ urlDetailed: imageUrl, urlThumbnail: imageUrl }] : []),
          videos: [],
          featureBulletPoints: [],
          faq: [],
        };
        productData.value = match;
        // No ancestry-insights validation: the store has already decided this barcode can
        // buy it, and validateInsights does not know these SKUs.
        if (match.purchased) {
          isValid.value = false;
          alreadyPurchased.value = true;
        }
        resolvedAsUpgrade = true;
      }
    }
    if (!resolvedAsUpgrade) {
      const res = await ProductService.getProduct(route.params.slug);
      productPrice.value = res;
      productData.value = res;

      if (productData.value.category === "Vitamins supplement") {
        await loadVitaminReport();
      }

      // Kits have no prerequisites — skip validation entirely.
      if (productData.value.category !== 'Kit') {
        try {
          const validationRes = await ProductService.validateInsights(
            productData.value?.variants[0]?.sku,
            store.getters.profile.profile.barcode
          );
          isValid.value = validationRes.valid;
          alreadyPurchased.value = validationRes.purchased === true;
        } catch (err) {
          console.error("Validation error:", err);
          if (err.code === "ERR_NETWORK") {
            console.log("Network error:", err.code);
          }
        }
      }
    }
  } catch (err) {
    console.error("Product loading error:", err);
    if (err.code === "ERR_NETWORK") {
      console.log("Network error:", err.code);
    }
  } finally {
    finishLoading();
  }
});

const openPaymentModal = (value = null) => {
  contactDetails.value = {
    ...contactDetails.value,
    ...value
  };
  delivered.value = value;
  isPaymentVisible.value = true;
  isCheckoutVisible.value = false;
}

const openPaymentCardModal = (updatedContactDetails) => {
  isPaymentCardVisible.value = true;
  isPaymentVisible.value = false;
  contactDetails.value = updatedContactDetails;
};

const closeCheckoutModal = () => {
  isCheckoutVisible.value = false;
}

const openCheckoutModal = async () => {
  if (productData.value.category === "Vitamins supplement") {
    try {
      // Ensure vitamin report is loaded
      const report = await loadVitaminReport();

      if (!report?.vitaminMix) {
        console.error('Vitamin mix not available in report');
        // You might want to show an error message to the user here
        return;
      }

      const url = createVitlabUrl({
        vitlabString: report.vitaminMix,
        profile: store.getters.profile.profile,
        user: store.getters.account,
        variant: productData.value.variants[0],
      });

      window.open(url, '_blank')?.focus();
    } catch (error) {
      console.error('Error opening vitamin checkout:', error);
      // Handle error - show message to user
    }
  } else if (isDigitalUpgrade.value) {
    // Digital upgrade: nothing is shipped, so skip the address form and go straight to
    // payment. Send name + a region-appropriate country (for currency); Stripe collects the
    // card postcode itself. The store checkout no longer requires an address for
    // non-shippable items.
    const profile = store.getters?.profile?.profile;
    const fullName = profile?.name || '';
    const spaceIdx = fullName.indexOf(' ');
    contactDetails.value = {
      ...contactDetails.value,
      firstName: spaceIdx > -1 ? fullName.slice(0, spaceIdx) : fullName,
      lastName: spaceIdx > -1 ? fullName.slice(spaceIdx + 1) : '',
      countryCode: COUNTRY_BY_REGION[selectedRegion.value] || 'GB',
    };
    delivered.value = contactDetails.value;
    isPaymentVisible.value = true;
  } else {
    isCheckoutVisible.value = true;
  }
}

// function to handle opening checkout modal from payment modal
const openCheckoutModalFromPayment = (contactDetailsFromPayment) => {
  console.log('Opening checkout modal from payment with details:', contactDetailsFromPayment);
  
  // Update contact details with the ones from payment modal
  if (contactDetailsFromPayment) {
    contactDetails.value = {
      ...contactDetails.value,
      ...contactDetailsFromPayment
    };
  }
  
  // Close payment modal and open checkout modal
  isPaymentVisible.value = false;
  // Use timeout to ensure modal state changes properly
  setTimeout(() => {
    isCheckoutVisible.value = true;
  }, 50);
};

const closePaymentModal = () => {
  isPaymentVisible.value = false;
}

const back = () => {
  isCheckoutVisible.value = false;
}

const returnStore = () => {
  router.push({ name: 'HealthStore' });
}

const upgradeWellbeing = () => {
  window.location.href = '/ancestry/details/Wellbeing-Upgrade';
}

const order = async (value = null) => {
  if (value?.order) {
    orderKey.value = value.order.orderKey;
  }
}

const PaymentcardbackModal = () => {
  isCheckoutVisible.value = true;
  isPaymentVisible.value = false;
}

const PaymentbackModal = () => {
  isPaymentVisible.value = true;
  isPaymentCardVisible.value = false;
}

const closeCardModal = () => {
  isPaymentCardVisible.value = false;
}

const closePaymentPaypalModal = () => {
  isPaymentVisible.value = false;
  isSuccessVisible.value = true;
}

const closePaymentCardModal = (value = null) => {
  isPaymentCardVisible.value = false;
  if (value?.paymentIntent?.status === "succeeded") {
    isSuccessVisible.value = true;
  } else {
    isFailedVisible.value = true;
  }
}

const closeFailedModal = () => {
  isFailedVisible.value = false;
}

/**
 * The "You will receive" points, as a list.
 *
 * Craft sends this as an array of strings - productBulletsMap returns one row per point -
 * and the markup used to bind the array straight to v-html, which stringifies it. Every
 * product with more than one point rendered as "Overall Biological Age,Pace of Aging,..."
 * on a single line.
 *
 * A string is still accepted because that is what v-html was written for, and nothing
 * guarantees every product in either CMS answers the same way.
 */
const featureBulletPoints = computed(() => {
  const raw = productPrice.value?.featureBulletPoints;
  if (Array.isArray(raw)) {
    return raw.filter((point) => point != null && String(point).trim() !== "");
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    return [raw];
  }
  return [];
});

const priceText = computed(() => {
  // Resolved server side for the requested region, so it and displayCurrencySymbol can
  // never name different stores.
  const resolvedPrice = productData.value?.pricing?.price;
  if (resolvedPrice != null && resolvedPrice !== 0) return resolvedPrice / 100;

  const priceKey = PRICE_KEY_BY_REGION[selectedRegion.value] || "price";
  const regionPrice = productData.value?.[priceKey];
  if (regionPrice != null && regionPrice !== 0) return regionPrice / 100;

  // Fallback: use variants[0] base price (Craft products carry GBP price here)
  const variant = productData.value.variants?.[0];
  if (!variant?.price) return 0;
  if (variant.price.isOnSale && variant.price.sale) return variant.price.sale / 100;
  if (variant.price.base) return variant.price.base / 100;
  return 0;
});

// Handle address update
const handleAddressUpdate = (address) => {
  contactDetails.value = { ...contactDetails.value, ...address };
};
</script>

<style lang="scss">
.gallery-video {
  width: 100%;
  height: 100%;
  min-height: 400px; /* adjust to match your gallery-image height */
  border: none;
}

.video-slide {
  flex-direction: column;
  gap: 8px;
}

.video-description {
  font-size: 14px;
  color: #555;
  text-align: center;
  padding: 4px 8px;
}

.video-thumbnail {
  position: relative;
}

.play-icon-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  pointer-events: none;
}

.carousel__item {
  width: 100%;
  background-color: var(--brand-color);
  color: #fff;
  font-size: 20px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.thumbnail-slider {
  padding-top: 15px;

  .thumbnail__item {
    width: 100%;
    padding-inline: 6px;

    img {
      aspect-ratio: 3/2;
      width: 100%;
      object-fit: cover;
      object-position: top;
      border-radius: 5px;
      height: auto;
    }
  }
}

.gallery-image {
  height: auto !important;
  aspect-ratio: 3 / 2;
}

#thumbnails {
  margin-top: 10px;
}

.carousel__item img {
  width: 100%;
  border-radius: 5px;
  object-fit: cover;
}

.gallery-image {
  height: 300px;
}

.thumbnail-image {
  height: 100px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.3s ease-in-out;
}

.thumbnail-image:hover {
  opacity: 1;
}
</style>